import axios from 'axios';
import { extractErrorMessage } from '../../utils/errorUtils';
import env from 'react-dotenv';
import {
  UserLogInRequest,
  UserLogoutRequest,
  superUserIntoClientRequest,
  resellerLoginAsClientRequest,
  MembershipDetectResponse,
  LoginWebappRequest,
  LoginWebappResponse,
} from '../../interfaces/UserInfo/userTypes';
import axiosInstance, { getApiTimeoutMs } from '../../utils/axiosInstance';

const API_BASE_URL = env.REACT_APP_API_BASE_URL;
// withCredentials lets the login response set the httpOnly refresh cookie
// (same-origin in prod; allowlisted cross-origin in local dev).
const rawAxiosConfig = () => ({
  timeout: getApiTimeoutMs(),
  withCredentials: true,
});
const isSuccessStatus = (statusCode?: number) =>
  typeof statusCode === 'number' && statusCode >= 200 && statusCode < 300;

export async function Login(loginDetails: UserLogInRequest) {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/auth/login-webapp`,
      loginDetails,
      rawAxiosConfig()
    );
    return response.data;
  } catch (error) {
    console.error('Error logging in:', error);
    throw error;
  }
}

// New auth flow functions
export async function membershipDetect(
  email: string,
  password: string
): Promise<MembershipDetectResponse> {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/auth/membership-detect`,
      { email, password },
      rawAxiosConfig()
    );
    return response.data;
  } catch (error) {
    console.error('Error in membership detect:', error);
    throw error;
  }
}

export async function loginWebapp(
  request: LoginWebappRequest
): Promise<LoginWebappResponse> {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/auth/login-webapp`,
      request,
      rawAxiosConfig()
    );
    return response.data;
  } catch (error) {
    console.error('Error in login webapp:', error);
    throw error;
  }
}

export const Logout = async (data: UserLogoutRequest) => {
  try {
    const response = await axiosInstance.post(`/auth/logout-webapp`, data);
    return response.data;
  } catch (error: unknown) {
    console.error('Error in Logout:', extractErrorMessage(error));
    // Return error response data instead of throwing
    throw error;
  }
};

/** Global sign-out — ends the account's sessions on every device. */
export const LogoutAllDevices = async (data: UserLogoutRequest) => {
  try {
    const response = await axiosInstance.post(`/auth/logout-all-devices`, data);
    return response.data;
  } catch (error: unknown) {
    console.error('Error in LogoutAllDevices:', extractErrorMessage(error));
    throw error;
  }
};

export const superUserIntoClient = async (data: superUserIntoClientRequest) => {
  const response = await axiosInstance.post(
    `/auth/super-user-login-as-client`,
    data
  );
  return response.data;
};

export const superUserLogoutOfClient = async (
  data: superUserIntoClientRequest
) => {
  const response = await axiosInstance.post(
    `/auth/super-user-logout-of-client`,
    data
  );
  return response.data;
};

export const resellerLoginAsClient = async (
  data: resellerLoginAsClientRequest
) => {
  const response = await axiosInstance.post(
    `/auth/reseller-login-as-client`,
    data
  );
  return response.data;
};

/** Exit reseller impersonation: re-mints a token acting as the caller's own company. */
export const resellerLogoutOfClient = async (data: {
  reseller_company_id: string;
}) => {
  const response = await axiosInstance.post(
    `/auth/reseller-logout-of-client`,
    data
  );
  return response.data;
};

export const sendOtpToEmail = async (email: string) => {
  try {
    const response = await axiosInstance.post(
      `${API_BASE_URL}/user/forgot-password`,
      { email }
    );

    if (isSuccessStatus(response.data.statusCode)) {
      return {
        success: true,
        data: response.data.data,
        message: response.data.message,
        error: null,
      };
    }

    return {
      success: false,
      data: null,
      message: response.data.message,
      error: response.data.error,
    };
  } catch (error: unknown) {
    const errorMessage = extractErrorMessage(
      error,
      'Email not found in the system'
    );
    // Try to extract status code from error if available
    const statusCode =
      error && typeof error === 'object' && 'response' in error
        ? (error as { response?: { status?: number } }).response?.status || 400
        : 500;

    return {
      success: false,
      data: null,
      message: errorMessage,
      error: errorMessage,
      statusCode,
    };
  }
};

export const verifyOtp = async (email: string, otp: string) => {
  try {
    const response = await axiosInstance.post(
      `${API_BASE_URL}/otp/verify-otp-email`,
      { email, otp }
    );

    if (isSuccessStatus(response.data.statusCode)) {
      const { tempOtpToken } = response.data.data;
      return {
        success: true,
        tempOtpToken,
        data: response.data.data,
        message: response.data.message,
        error: null,
      };
    }

    return {
      success: false,
      data: null,
      message: response.data.message,
      error: response.data.error,
    };
  } catch (error: unknown) {
    const errorMessage = extractErrorMessage(error);
    // Try to extract status code from error if available
    const statusCode =
      error && typeof error === 'object' && 'response' in error
        ? (error as { response?: { status?: number } }).response?.status || 400
        : 500;

    return {
      success: false,
      data: null,
      message: errorMessage,
      error: errorMessage,
      statusCode,
    };
  }
};

export const validatePasswordToken = async (token: string) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/otp/validate-token`,
      { token },
      {
        ...rawAxiosConfig(),
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (isSuccessStatus(response.data.statusCode)) {
      return {
        success: true,
        data: response.data.data,
        message: response.data.message,
      };
    }

    return {
      success: false,
      message: response.data.message,
    };
  } catch (error: unknown) {
    return {
      success: false,
      message: extractErrorMessage(error, 'Failed to validate token'),
    };
  }
};

export const resetPassword = async (password: string, token: string) => {
  try {
    const response = await axiosInstance.post(
      `${API_BASE_URL}/user/reset-password`,
      { password, token }
    );

    if (response.data.statusCode === 200) {
      return {
        success: true,
        data: response.data.data,
        message: response.data.message,
        error: null,
      };
    }

    return {
      success: false,
      data: null,
      message: response.data.message,
      error: response.data.error,
    };
  } catch (error: unknown) {
    const errorMessage = extractErrorMessage(error, 'Failed to reset password');
    // Try to extract status code from error if available
    const statusCode =
      error && typeof error === 'object' && 'response' in error
        ? (error as { response?: { status?: number } }).response?.status || 400
        : 500;

    return {
      success: false,
      data: null,
      message: errorMessage,
      error: errorMessage,
      statusCode,
    };
  }
};

export const acceptTermsAndConditions = async (
  userId: string,
  token: string
) => {
  try {
    const response = await axiosInstance.post(
      `${API_BASE_URL}/user/accept-terms`,
      { user_id: userId },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (response.data.statusCode === 200) {
      return {
        success: true,
        data: response.data.data,
        message: response.data.message,
        error: null,
      };
    }

    return {
      success: false,
      data: null,
      message: response.data.message,
      error: response.data.error,
    };
  } catch (error: unknown) {
    const errorMessage = extractErrorMessage(
      error,
      'Failed to accept terms and conditions'
    );
    // Try to extract status code from error if available
    const statusCode =
      error && typeof error === 'object' && 'response' in error
        ? (error as { response?: { status?: number } }).response?.status || 400
        : 500;

    return {
      success: false,
      data: null,
      message: errorMessage,
      error: errorMessage,
      statusCode,
    };
  }
};

export const confirmUnsubscribe = async (token: string) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/user/confirm-unsubscribe`,
      { token },
      rawAxiosConfig()
    );

    if (response.data.statusCode === 200 || response.data.statusCode === 201) {
      return {
        success: true,
        data: response.data.data,
        message: response.data.message,
        error: null,
      };
    }

    return {
      success: false,
      data: null,
      message: response.data.message,
      error: response.data.error,
    };
  } catch (error: unknown) {
    const errorMessage = extractErrorMessage(error, 'Failed to unsubscribe');
    // Try to extract status code from error if available
    const statusCode =
      error && typeof error === 'object' && 'response' in error
        ? (error as { response?: { status?: number } }).response?.status || 400
        : 500;

    return {
      success: false,
      data: null,
      message: errorMessage,
      error: errorMessage,
      statusCode,
    };
  }
};
